import {
  buildUniqueIdMap,
  REMAPPED_UNIQUE_ID_LENGTH,
  remapUniqueIds,
  RemapUniqueIdsOptions
} from '@alpinehomeair/common';
import { LevelType, TruncatedLevelType } from 'data/types';
import type { ProjectState } from 'features/project/project-slice';
import type { StructureState } from 'features/structure/structure-slice';
import { nanoid } from 'nanoid';
import {
  ExportedConfigurationJson,
  ExportedProjectJson,
  ParsedStructureLevelsJson,
  StructureExportInput,
  StructureLevelsJson
} from 'types/structure-json';
import { truncateAttributes } from 'utils/attributes';
import { transformConfigurationsForExport } from 'utils/configuration-export';
import {
  restoreStructureAttributes,
  revalidateStructureSizingAttributes
} from 'utils/structure';

export const STRUCTURE_JSON_VERSION = 2;

const IMPORT_REMAP_OPTIONS: RemapUniqueIdsOptions = {
  unmappedReferences: 'keep'
};

export function buildExportedProjectJson(
  project: ProjectState,
  structure: StructureState
): ExportedProjectJson {
  return {
    projectSettings: truncateAttributes(project.projectSettings),
    addressId: project.addressId,
    propertyLocation: project.propertyLocation,
    locationData: project.locationData,
    attomData: project.attomData,
    weatherData: project.weatherData,
    structureTypeId: structure.structureTypeId,
    structureSubtypeId: structure.structureSubtypeId,
    partialStructure: structure.partialStructure,
    conditionedLevels: structure.conditionedLevels,
    existingSystems: structure.existingSystems,
    notes: structure.notes
  };
}

function truncateStructureLevels(levels: LevelType[]): TruncatedLevelType[] {
  return levels.map((level) => ({
    ...level,
    attributes: truncateAttributes(level.attributes),
    sections: level.sections.map((section) => ({
      ...section,
      attributes: truncateAttributes(section.attributes),
      spaces: section.spaces.map((space) => ({
        ...space,
        attributes: truncateAttributes(space.attributes)
      }))
    }))
  }));
}

export function buildStructureExportJson(
  input: StructureExportInput
): StructureLevelsJson {
  const defaultLevelUniqueId = input.levels[0]?.uniqueId ?? '';

  return {
    version: STRUCTURE_JSON_VERSION,
    orientation: input.orientation ?? null,
    footprint: input.footprint ?? null,
    condenserLocations: input.condenserLocations ?? null,
    levels: truncateStructureLevels(input.levels),
    project: input.project,
    configurations: transformConfigurationsForExport(
      input.configurations,
      defaultLevelUniqueId
    )
  };
}

function sanitizeStructureFileName(name: string): string {
  const trimmed = name.trim().replaceAll(/[\\/:*?"<>|]+/g, '-');
  return trimmed.length > 0 ? trimmed : `structure-${nanoid(5)}`;
}

export function downloadStructureLevels(
  input: StructureExportInput,
  name?: string
): void {
  const payload = JSON.stringify(buildStructureExportJson(input), null, 2);
  const blob = new Blob([payload], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeStructureFileName(name ?? '')}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function parseStructureLevelsJson(content: string): StructureLevelsJson {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    throw new Error('Invalid JSON file');
  }

  if (Array.isArray(parsed)) {
    return { orientation: null, levels: parsed };
  }

  if (parsed && typeof parsed === 'object' && 'levels' in parsed) {
    return parsed as StructureLevelsJson;
  }

  throw new Error('Invalid structure JSON format');
}

/**
 * Mints fresh uniqueIds for every imported item (levels, sections, spaces,
 * electrical panels, sizing items, configurations and their children) and
 * remaps all internal references to them, including the project's references
 * to structure ids (conditionedLevels, existing-equipment levelUniqueIds).
 * The project's own embedded items keep their ids — only references into the
 * regenerated structure are remapped.
 *
 * Imported files keep the ids of the structure they were exported from. The
 * API upserts items globally by uniqueId, so reusing imported ids makes the
 * save update the ORIGINAL structure's rows instead of inserting rows for the
 * current structure — the imported levels are never attached to the current
 * structure (they vanish on reload) and later saves/deletes can corrupt or
 * delete the source structure's data.
 */
export function regenerateStructureImportUniqueIds(
  levels: TruncatedLevelType[],
  configurations: ExportedConfigurationJson[] | null,
  project: ExportedProjectJson | null = null
): {
  levels: TruncatedLevelType[];
  configurations: ExportedConfigurationJson[] | null;
  project: ExportedProjectJson | null;
} {
  const idMap = buildUniqueIdMap([levels, configurations], {
    createId: () => nanoid(REMAPPED_UNIQUE_ID_LENGTH)
  });

  return {
    levels: remapUniqueIds(levels, idMap, IMPORT_REMAP_OPTIONS),
    configurations: configurations
      ? remapUniqueIds(configurations, idMap, IMPORT_REMAP_OPTIONS)
      : null,
    project: project
      ? remapUniqueIds(project, idMap, IMPORT_REMAP_OPTIONS)
      : null
  };
}

export function parseAndExpandStructureLevelsJson(
  content: string
): ParsedStructureLevelsJson {
  const {
    orientation,
    footprint,
    condenserLocations,
    levels,
    project,
    configurations
  } = parseStructureLevelsJson(content);

  const regenerated = regenerateStructureImportUniqueIds(
    levels,
    configurations ?? null,
    project ?? null
  );

  return {
    orientation: orientation ?? null,
    footprint: footprint ?? null,
    condenserLocations: condenserLocations ?? null,
    levels: revalidateStructureSizingAttributes(
      restoreStructureAttributes(regenerated.levels)
    ),
    project: regenerated.project,
    configurations: regenerated.configurations
  };
}
